import { computed, onBeforeUnmount, ref, watch } from 'vue';
import ConversationIndexService from '@/core/conversations/conversation-index/conversation-index.service';

const LOAD_COOLDOWN_MS = 1000;
const OBSERVER_START_DELAY_MS = 500;

/**
 * Loads the next page of conversations when the sentinel element scrolls into view.
 * The observer's root is the sentinel's nearest scrolling ancestor, so it follows the
 * candidates list wherever that scroll container lives.
 *
 * @param sentinelId Id of the fixed-height element at the end of the list.
 */
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useConversationListInfiniteScroll(sentinelId: string) {
  const conversationIndexService = new ConversationIndexService();

  const hasStartedInfiniteScroll = ref(false);
  const infiniteScrollObserver = ref<IntersectionObserver | null>(null);
  const lastLoadTime = ref(0);
  let startTimer: ReturnType<typeof setTimeout> | undefined;

  const loadingConversationIndexes = computed(
    () => conversationIndexService.loadingConversationIndexes,
  );
  const hasConversations = computed(() => conversationIndexService.conversationIndexes.length > 0);

  function startObserver() {
    infiniteScrollObserver.value?.disconnect();

    infiniteScrollObserver.value = new IntersectionObserver((entries) => {
      if (loadingConversationIndexes.value || !entries[0].isIntersecting) {
        return;
      }
      const now = Date.now();

      if (now - lastLoadTime.value >= LOAD_COOLDOWN_MS) {
        lastLoadTime.value = now;
        conversationIndexService.searchMoreConversations();
      }
    });

    const loadMoreElement = document.querySelector(`#${sentinelId}`);
    if (loadMoreElement) {
      infiniteScrollObserver.value.observe(loadMoreElement);
    }
  }

  watch(loadingConversationIndexes, (value) => {
    if (!value && hasConversations.value && !hasStartedInfiniteScroll.value) {
      // An observer fires as soon as it starts observing, which would load a second page at once.
      clearTimeout(startTimer);
      startTimer = setTimeout(() => {
        startObserver();
        hasStartedInfiniteScroll.value = true;
      }, OBSERVER_START_DELAY_MS);
    }
  });

  onBeforeUnmount(() => {
    clearTimeout(startTimer);
    infiniteScrollObserver.value?.disconnect();
  });

  /** Call when the list is replaced so the observer restarts after the next load. */
  function resetInfiniteScroll() {
    hasStartedInfiniteScroll.value = false;
  }

  return { resetInfiniteScroll };
}
